import React, { useEffect, useState } from 'react';
import { Col, Form, Input, Modal, Row, Select } from 'antd';
import { EditRequestIcon } from '../../../../resource/icons';
import useServicesQuery from '../../hooks/useServicesQuery';
import useParentServices from '../../hooks/useParentServices';
import useWorkersQuery, { getWorkers } from '../../hooks/useWorkersQuery';
import {
  dspxGetRoomOptions,
  dspxGetWorkerChildrens,
} from '../../../../helpers/dspx-utils';
import useExecutersQuery, { getExecuters } from '../../hooks/useExecutersQuery';
import {
  MODAL_UNSAVED_DATA_WARNING_TITLE,
  UNSAVED_DATA_WARNING_TEXT,
} from '../../../../constants/text.constants';
import useCheckWarranty, {
  getCheckWarranty,
} from '../../hooks/useCheckWarranty';
import RequestChangeContent from '../RequestChangeContent/RequestChangeContent';
import useAddressMutation from '../../hooks/useAddressMutation';
import useRequestStreets from '../../../../queries/useRequestStreets';
import useRequestHouses from '../../../../queries/useRequestHouses';
import useRequestAddresses from '../../../../queries/useRequestAddresses';

const ServiceReasonFormNode = ({ form, request, onClose }) => {
  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
    resetFields,
    validateFields,
  } = form;

  const [isInitializing, setIsInitializing] = useState(true);

  const streetIdValue = getFieldValue('streetId');
  const houseIdValue = getFieldValue('houseId');
  const addressIdValue = getFieldValue('addressId');
  const parentServiceIdValue = getFieldValue('parentServiceId');
  const serviceIdValue = getFieldValue('serviceId');
  const masterIdValue = getFieldValue('masterId');
  const executerIdValue = getFieldValue('executerId');

  const isServiceOnly = !!request?.serviceOnly;
  const houseId = houseIdValue || request?.houseId;
  const addressId = addressIdValue || request?.addressId;

  const { mutateAsync: postAddress, isLoading } = useAddressMutation();

  //addresses query
  const { data: streets = [], isFetching: isFetchingStreets } =
    useRequestStreets({ enabled: !!(request && !isServiceOnly) });
  const { data: houses = [], isFetching: isFetchingHouses } = useRequestHouses(
    streetIdValue,
    { enabled: !!(request && !isServiceOnly && streetIdValue) }
  );
  const { data: flats = [], isFetching: isFetchingFlats } = useRequestAddresses(
    houseIdValue,
    {
      enabled: !!(request && !isServiceOnly && houseIdValue),
    }
  );

  // old values
  const { data: isWarrantyOld, isFetching: isFetchingWarrantyOld } =
    useCheckWarranty(
      { serviceId: request?.serviceId, houseId: request?.houseId },
      {
        enabled: !!request,
        select: (data) => data?.isWarranty,
      }
    );
  const isWarrantyLoadedOld =
    !isFetchingWarrantyOld && isWarrantyOld !== undefined;

  const { data: workersOld = [] } = useWorkersQuery(
    {
      houseId: request?.houseId,
      addressId: request?.addressId,
      requestId: request?.id,
      serviceId: request?.serviceId,
      isWarranty: isWarrantyOld,
    },
    {
      enabled: !!(request && isWarrantyLoadedOld),
    }
  );
  const { data: executersOld = [] } = useExecutersQuery(
    {
      houseId: request?.houseId,
      addressId: request?.addressId,
      requestId: request?.id,
      serviceId: request?.serviceId,
      isWarranty: isWarrantyOld,
    },
    {
      enabled: !!(request && isWarrantyLoadedOld),
    }
  );
  const { data: servicesOld = [] } = useServicesQuery(
    { parentId: request?.parentServiceId, houseId: request?.houseId },
    { enabled: !!request }
  );
  const { data: parentServicesOld = [] } = useParentServices(
    { houseId: request?.houseId },
    {
      enabled: !!request,
      staleTime: Infinity,
    }
  );

  // new values query
  const { data: parentServices = [], isFetching: isFetchingParentService } =
    useParentServices(
      { houseId: houseId },
      {
        enabled: !!(request && (!isServiceOnly ? houseIdValue : true)),
        staleTime: Infinity,
      }
    );
  const { data: services = [], isFetching: isFetchingService } =
    useServicesQuery(
      {
        parentId: parentServiceIdValue,
        houseId: houseId,
      },
      {
        enabled: !!(
          request &&
          parentServiceIdValue &&
          (!isServiceOnly ? houseIdValue : true)
        ),
      }
    );
  const { data: isWarranty, isFetching: isFetchingWarranty } = useCheckWarranty(
    { serviceId: serviceIdValue, houseId: houseId },
    {
      enabled: !!(
        request &&
        serviceIdValue &&
        (!isServiceOnly ? houseIdValue : true)
      ),
      select: (data) => data?.isWarranty,
    }
  );

  const isWarrantyLoaded = !isFetchingWarranty && isWarranty !== undefined;

  const { data: workers = [], isFetching: isFetchingWorkers } = useWorkersQuery(
    {
      houseId: houseId,
      addressId: addressId,
      requestId: request?.id,
      serviceId: serviceIdValue,
      isWarranty,
    },
    {
      enabled: !!(
        request &&
        serviceIdValue &&
        !!isWarrantyLoaded &&
        (!isServiceOnly ? !!(houseIdValue && addressIdValue) : true)
      ),
    }
  );
  const { data: executers = [], isFetching: isFetchingExecuters } =
    useExecutersQuery(
      {
        houseId: houseId,
        addressId: addressId,
        requestId: request?.id,
        serviceId: serviceIdValue,
        isWarranty,
      },
      {
        enabled: !!(
          request &&
          serviceIdValue &&
          !!isWarrantyLoaded &&
          (!isServiceOnly ? !!(houseIdValue && addressIdValue) : true)
        ),
      }
    );

  const resetAndClose = () => {
    resetFields();
    setIsInitializing(true);
    onClose();
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: MODAL_UNSAVED_DATA_WARNING_TITLE,
        content: UNSAVED_DATA_WARNING_TEXT,
        okType: 'danger',
        okText: 'Да',
        cancelText: 'Отмена',
        onOk: resetAndClose,
      });
      return;
    }

    resetAndClose();
  };

  const showConfirm = (values) => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: `Изменение услуги/причины`,
        content: (
          <RequestChangeContent
            values={values}
            request={request}
            workersOld={workersOld}
            executersOld={executersOld}
            workers={workers}
            executers={executers}
            parentServices={parentServices}
            services={services}
            oldServices={servicesOld}
            parentServicesOld={parentServicesOld}
          />
        ),
        width: 500,
        okText: 'Да',
        cancelText: 'Отмена',
        onOk: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
        onClose: () => {
          resolve(false);
        },
      });
    });
  };

  const handleSave = async (values) => {
    const addressIdToSave = values.addressId || request?.addressId;

    const result = await postAddress({
      addressId: addressIdToSave,
      executorId: values.executerId,
      masterId: values.masterId,
      serviceId: values.serviceId,
      requestId: request?.id,
    });

    if (result === 'Error') return;

    resetAndClose();
  };

  const submitForm = async (values) => {
    const isReasonChanged = request?.serviceId !== Number(serviceIdValue);
    const isWorkerChanged =
      Number(values?.masterId) !== Number(request?.master?.id) ||
      Number(values?.executerId) !== Number(request?.executer?.id);

    if (isReasonChanged && isWorkerChanged) {
      const confirmed = await showConfirm(values);
      if (!confirmed) return;
    }

    await handleSave(values);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    validateFields((err, values) => {
      if (err) return;
      submitForm(values);
    });
  };

  useEffect(() => {
    if (request) {
      setIsInitializing(true);
    }
  }, [request]);

  useEffect(() => {
    if (!isInitializing || !streetIdValue || isServiceOnly) {
      return;
    }

    setFieldsValue({
      houseId: request?.houseId || undefined,
    });
  }, [isInitializing, streetIdValue]);

  useEffect(() => {
    if (!isInitializing || !houseIdValue || isServiceOnly) {
      return;
    }

    setFieldsValue({
      addressId: request?.addressId ? String(request.addressId) : undefined,
    });
  }, [isInitializing, houseIdValue]);

  useEffect(() => {
    if (!isInitializing || !addressIdValue || isServiceOnly) {
      return;
    }

    setFieldsValue({
      parentServiceId: request?.parentServiceId || undefined,
    });
  }, [isInitializing, addressIdValue]);

  useEffect(() => {
    if (!isInitializing || !parentServiceIdValue) {
      return;
    }

    setFieldsValue({
      serviceId: request?.serviceId || undefined,
    });
  }, [isInitializing, parentServiceIdValue]);

  useEffect(() => {
    if (!isInitializing || !serviceIdValue) {
      return;
    }

    setFieldsValue({
      masterId: request?.master?.id ? String(request.master.id) : undefined,
      executerId: request?.executer?.id
        ? String(request.executer.id)
        : undefined,
    });
  }, [isInitializing, serviceIdValue]);

  useEffect(() => {
    if (!isInitializing) {
      return;
    }

    if (
      !isServiceOnly
        ? !(streetIdValue && houseIdValue && addressIdValue)
        : false
    ) {
      return;
    }

    if (!serviceIdValue) {
      return;
    }

    if (isFetchingWorkers || isFetchingExecuters) {
      return;
    }

    if (request?.master?.id && !masterIdValue) {
      return;
    }

    if (request?.executer?.id && !executerIdValue) {
      return;
    }

    setIsInitializing(false);
  }, [
    isInitializing,
    serviceIdValue,
    masterIdValue,
    executerIdValue,
    request,
    isFetchingWorkers,
    isFetchingExecuters,
    streetIdValue,
    houseIdValue,
    addressIdValue,
  ]);

  const changeService = async (value) => {
    setFieldsValue({
      serviceId: value,
    });

    if (isInitializing || !value) return;

    const isWarrantyCheck = await getCheckWarranty({
      serviceId: value,
      houseId: houseId,
    });

    const work = await getWorkers({
      houseId: houseId,
      addressId: addressId,
      requestId: request?.id,
      serviceId: value,
      isWarranty: isWarrantyCheck?.isWarranty,
    });
    const exe = await getExecuters({
      houseId: houseId,
      addressId: addressId,
      requestId: request?.id,
      serviceId: value,
      isWarranty: isWarrantyCheck?.isWarranty,
    });

    const shouldAutoSetWorker = work.length > 0 && work[0]?.autoSet;
    const shouldAutoSetExecuter = exe.length > 0 && exe[0]?.autoSet;

    setFieldsValue({
      masterId: shouldAutoSetWorker ? String(work[0].id) : undefined,
      executerId: shouldAutoSetExecuter ? String(exe[0].id) : undefined,
    });
  };

  const changeStreet = () => {
    if (isInitializing) {
      return;
    }

    setFieldsValue({
      addressId: undefined,
      houseId: undefined,
      parentServiceId: undefined,
      serviceId: undefined,
      masterId: undefined,
      executerId: undefined,
    });
  };

  const changeHouse = () => {
    if (isInitializing) {
      return;
    }

    setFieldsValue({
      addressId: undefined,
      parentServiceId: undefined,
      serviceId: undefined,
      masterId: undefined,
      executerId: undefined,
    });
  };

  const changeParentService = () => {
    if (isInitializing) {
      return;
    }

    setFieldsValue({
      serviceId: undefined,
      masterId: undefined,
      executerId: undefined,
    });
  };

  return (
    <Modal
      width={520}
      title={
        <span>
          <EditRequestIcon
            style={{ height: 40, verticalAlign: 'middle', marginRight: 10 }}
          />
          Изменение заявки
        </span>
      }
      visible={!!request}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={isLoading}
      destroyOnClose
      okText="Сохранить"
    >
      <Form>
        {!isServiceOnly && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Адрес">
                <Input.Group compact>
                  {getFieldDecorator('streetId', {
                    initialValue: request?.streetId || undefined,
                    rules: [
                      {
                        required: true,
                        message: 'Пожалуйста, выберите адрес.',
                      },
                    ],
                  })(
                    <Select
                      showSearch
                      style={{ width: 248 }}
                      placeholder="Улица"
                      notFoundContent="нет данных"
                      optionLabelProp="label"
                      optionFilterProp="searchtext"
                      onChange={changeStreet}
                      disabled={isFetchingStreets}
                      loading={isFetchingStreets}
                    >
                      {streets?.map((street) => (
                        <Select.Option
                          key={street.id}
                          value={street.id}
                          searchtext={street.name}
                          label={street.name}
                        >
                          {street.name}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                  {getFieldDecorator('houseId', {
                    rules: [
                      {
                        required: true,
                        message: 'Пожалуйста, выберите адрес.',
                      },
                    ],
                  })(
                    <Select
                      showSearch
                      style={{ width: 100 }}
                      notFoundContent="нет данных"
                      placeholder="Дом"
                      optionFilterProp="searchtext"
                      onChange={changeHouse}
                      disabled={isFetchingHouses}
                      loading={isFetchingHouses}
                    >
                      {houses?.map((house) => (
                        <Select.Option
                          key={house.id}
                          value={house.id}
                          searchtext={house.name}
                          label={house.name}
                        >
                          {house.name}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                  {getFieldDecorator('addressId', {
                    rules: [
                      {
                        required: true,
                        message: 'Пожалуйста, выберите адрес.',
                      },
                    ],
                  })(
                    <Select
                      showSearch
                      notFoundContent="нет данных"
                      style={{ width: 120 }}
                      placeholder="Помещения"
                      dropdownMatchSelectWidth={false}
                      optionFilterProp="searchtext"
                      disabled={isFetchingFlats}
                      loading={isFetchingFlats}
                    >
                      {dspxGetRoomOptions(flats)}
                    </Select>
                  )}
                </Input.Group>
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Услуга">
              {getFieldDecorator('parentServiceId', {
                initialValue: request?.parentServiceId || undefined,
                rules: [
                  { required: true, message: 'Пожалуйста, выберите услугу' },
                ],
              })(
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder="услуга"
                  notFoundContent="нет данных"
                  optionFilterProp="searchtext"
                  disabled={
                    isFetchingParentService ||
                    (!isServiceOnly && !addressIdValue)
                  }
                  loading={isFetchingParentService}
                  onChange={changeParentService}
                >
                  {parentServices?.map((item) => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      searchtext={item.name}
                      label={item.name}
                    >
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              )}
              {getFieldDecorator('serviceId', {
                rules: [
                  { required: true, message: 'Пожалуйста, выберите причину' },
                ],
              })(
                <Select
                  showSearch
                  style={{ width: 266 }}
                  placeholder="причина"
                  notFoundContent="нет данных"
                  optionFilterProp="searchtext"
                  disabled={
                    isFetchingService || (!isServiceOnly && !addressIdValue)
                  }
                  loading={isFetchingService}
                  onChange={changeService}
                >
                  {services?.map((item) => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      searchtext={item.name}
                      label={item.name}
                    >
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Мастер">
              {getFieldDecorator('masterId')(
                <Select
                  showSearch
                  allowClear={true}
                  placeholder="мастер"
                  notFoundContent="нет данных"
                  optionLabelProp="label"
                  optionFilterProp="searchtext"
                  disabled={
                    isFetchingWorkers || (!isServiceOnly && !addressIdValue)
                  }
                  loading={isFetchingWorkers}
                >
                  {dspxGetWorkerChildrens(workers)}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Исполнитель">
              {getFieldDecorator('executerId')(
                <Select
                  showSearch
                  allowClear={true}
                  placeholder="исполнитель"
                  notFoundContent="нет данных"
                  optionLabelProp="label"
                  optionFilterProp="searchtext"
                  disabled={
                    isFetchingExecuters || (!isServiceOnly && !addressIdValue)
                  }
                  loading={isFetchingExecuters}
                >
                  {dspxGetWorkerChildrens(executers)}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const ServiceReasonForm = Form.create('service-reason-form')(
  ServiceReasonFormNode
);
export default ServiceReasonForm;
